import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  DeleteOutline as DeleteIcon,
  DownloadOutlined as DownloadIcon,
  EditOutlined as EditIcon,
  AccountBalanceOutlined as BankIcon,
  CreditCardOutlined as AccountIcon,
  QrCode2Outlined as IfscIcon,
  Numbers as NumbersIcon,
  FileUploadOutlined as ImportIcon,
  IosShareOutlined as ExportIcon,
  SearchOutlined as SearchIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';
import { usePagePermissions } from '../context/AuthContext';

const initialRows = [];

function downloadTextFile({ filename, content, mime = 'text/plain;charset=utf-8' }) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function toCsvValue(v) {
  const s = String(v ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replaceAll('"', '""')}"`;
  }
  return s;
}

function parseCsvText(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const rows = [];
  for (const line of lines) {
    const cells = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        const next = line[i + 1];
        if (inQuotes && next === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }
      if (ch === ',' && !inQuotes) {
        cells.push(cur.trim());
        cur = '';
        continue;
      }
      cur += ch;
    }
    cells.push(cur.trim());
    rows.push(cells);
  }
  return rows;
}

function normalizeHeaderCell(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/_/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function looksLikeExcelFile(file) {
  const name = String(file?.name || '').toLowerCase();
  return name.endsWith('.xlsx') || name.endsWith('.xls');
}

async function parseExcelFileToRows(file) {
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheetName = wb.SheetNames?.[0];
  const ws = wb.Sheets?.[sheetName];
  if (!ws) return [];
  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  return Array.isArray(aoa) ? aoa : [];
}

export default function VendorManagement() {
  const fileInputRef = useRef(null);

  const {
    canCreate,
    canUpdate,
    canDelete,
    canImport,
    canExport,
  } = usePagePermissions(['vendor_master', 'vendors', 'vendor_management']);

  const [rows, setRows] = useState(() => initialRows);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [filterKind, setFilterKind] = useState('');
  const [filterVendorCode, setFilterVendorCode] = useState('');
  const [filterBankCode, setFilterBankCode] = useState('');

  const [orderBy, setOrderBy] = useState('firmVendorName');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [addPageOpen, setAddPageOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [formKind, setFormKind] = useState('vendor');
  const [formFirmVendorCode, setFormFirmVendorCode] = useState('');
  const [formFirmVendorBankCode, setFormFirmVendorBankCode] = useState('');
  const [formFirmVendor, setFormFirmVendor] = useState('');
  const [formBankName, setFormBankName] = useState('');
  const [formAccountNumber, setFormAccountNumber] = useState('');
  const [formIfsc, setFormIfsc] = useState('');
  const [formContactDetails, setFormContactDetails] = useState('');
  const [formVendorContactPerson, setFormVendorContactPerson] = useState('');
  const [formVendorContactPhone, setFormVendorContactPhone] = useState('');
  const [formApplicableCommissionPct, setFormApplicableCommissionPct] = useState('');
  const [formGstNumber, setFormGstNumber] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [autoCodeEnabled, setAutoCodeEnabled] = useState(true);
  const [newBankCode, setNewBankCode] = useState('');
  const [newBankName, setNewBankName] = useState('');
  const [newAccountNumber, setNewAccountNumber] = useState('');
  const [newIfsc, setNewIfsc] = useState('');
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [bankDialogVendor, setBankDialogVendor] = useState(null);
  const [bankAccountsMap, setBankAccountsMap] = useState({});
  const [bankCodeViewOpen, setBankCodeViewOpen] = useState(false);
  const [bankCodeView, setBankCodeView] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [deleteRowId, setDeleteRowId] = useState(null);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const loadVendors = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vendors`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load vendors');
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Failed to load vendors', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVendors();
    loadBankAccounts();
  }, []);

  const loadBankAccounts = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/vendor-bank-accounts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` }
      });
      const data = await res.json().catch(() => ({}));
      const items = Array.isArray(data?.items) ? data.items : [];
      const next = {};
      items.forEach((it) => {
        const code = String(it.vendorCode || '');
        if (!next[code]) next[code] = [];
        next[code].push(it);
      });
      setBankAccountsMap(next);
    } catch {
      setBankAccountsMap({});
    }
  };

  const filterOptions = useMemo(() => {
    const kinds = new Set();
    const vendorCodes = new Set();
    const bankCodes = new Set();
    rows.forEach((r) => {
      const k = String(r.firmVendor || '').toLowerCase();
      if (k) kinds.add(k);
      const vc = String(r.firmVendorCode || '');
      if (vc) vendorCodes.add(vc);
      const bc = String(r.firmVendorBankCode || '');
      if (bc) bankCodes.add(bc);
    });
    return {
      kinds: Array.from(kinds).sort(),
      vendorCodes: Array.from(vendorCodes).sort(),
      bankCodes: Array.from(bankCodes).sort(),
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    const kind = String(filterKind || '').trim().toLowerCase();
    const vc = String(filterVendorCode || '').trim();
    const bc = String(filterBankCode || '').trim();
    return rows.filter((r) => {
      const code = String(r.firmVendorCode || '');
      const bankCode = String(r.firmVendorBankCode || '');
      const name = String(r.firmVendorName || r.firmVendor || '');
      const contact = String(r.vendorContactPerson || '');
      const matchesQuery =
        !q ||
        code.toLowerCase().includes(q) ||
        bankCode.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q) ||
        contact.toLowerCase().includes(q);
      const matchesKind = !kind || String(r.firmVendor || '').toLowerCase() === kind;
      const matchesVendorCode = !vc || code === vc;
      const matchesBankCode = !bc || bankCode === bc;
      return matchesQuery && matchesKind && matchesVendorCode && matchesBankCode;
    });
  }, [rows, query, filterKind, filterVendorCode, filterBankCode]);

  const groupedRows = useMemo(() => {
    const map = new Map();
    for (const r of filteredRows) {
      const code = String(r.firmVendorCode || '');
      if (!map.has(code)) {
        map.set(code, {
          firmVendorCode: code,
          firmVendorName: r.firmVendorName || r.firmVendor || '',
          vendorContactPerson: r.vendorContactPerson || '',
          bankCodes: new Set(),
          firstRow: r,
        });
      }
      const entry = map.get(code);
      entry.bankCodes.add(String(r.firmVendorBankCode || ''));
    }
    return Array.from(map.values()).map((e) => {
      const extra = Array.isArray(bankAccountsMap[e.firmVendorCode])
        ? bankAccountsMap[e.firmVendorCode].map((x) => String(x.bankCode || ''))
        : [];
      extra.forEach((bc) => e.bankCodes.add(bc));
      return {
        id: e.firstRow.id,
        firmVendorCode: e.firmVendorCode,
        firmVendorName: e.firmVendorName,
        vendorContactPerson: e.vendorContactPerson,
        firmVendorBankCodes: Array.from(e.bankCodes).sort().join(','),
        firstRow: e.firstRow,
      };
    });
  }, [filteredRows, bankAccountsMap]);

  const getAllBankCodesForVendor = (code, primary) => {
    const s = new Set();
    const vc = String(code || '').trim();
    const p = String(primary || '').trim();
    if (p) s.add(p);
    rows.forEach((r) => {
      if (String(r.firmVendorCode || '') === vc) {
        const bc = String(r.firmVendorBankCode || '').trim();
        if (bc) s.add(bc);
      }
    });
    const extras = Array.isArray(bankAccountsMap[vc])
      ? bankAccountsMap[vc].map((x) => String(x.bankCode || '').trim())
      : [];
    extras.forEach((bc) => {
      if (bc) s.add(bc);
    });
    return Array.from(s).sort();
  };

  const openBankCodeDetails = (vendorCode, primaryRow, code) => {
    const norm = (s) => String(s || '').trim().toLowerCase();
    const vc = norm(vendorCode);
    const bc = norm(code);
    let details = null;
    // 1) Primary row match
    if (primaryRow && norm(primaryRow.firmVendorBankCode) === bc && norm(primaryRow.firmVendorCode) === vc) {
      details = {
        vendorCode: primaryRow.firmVendorCode || '',
        bankCode: primaryRow.firmVendorBankCode || '',
        vendorName: primaryRow.firmVendorName || primaryRow.firmVendor || '',
        bankName: primaryRow.bankName || '',
        accountNumber: primaryRow.accountNumber || '',
        ifsc: primaryRow.ifsc || '',
      };
    }
    // 2) Exact match in vendorcodesheet rows
    if (!details) {
      const foundRow = rows.find(
        (r) =>
          norm(r.firmVendorCode) === vc &&
          norm(r.firmVendorBankCode) === bc
      );
      if (foundRow) {
        details = {
          vendorCode: foundRow.firmVendorCode || '',
          bankCode: foundRow.firmVendorBankCode || '',
          vendorName: foundRow.firmVendorName || foundRow.firmVendor || '',
          bankName: foundRow.bankName || '',
          accountNumber: foundRow.accountNumber || '',
          ifsc: foundRow.ifsc || '',
        };
      }
    }

    // 3) Check bankAccountsMap
    if (!details && bankAccountsMap[vendorCode]) {
      const foundAccount = bankAccountsMap[vendorCode].find(
        (acc) => norm(acc.bankCode) === bc
      );
      if (foundAccount) {
        details = {
          vendorCode: vc,
          bankCode: bc,
          vendorName: primaryRow?.firmVendorName || primaryRow?.firmVendor || '',
          bankName: foundAccount.bankName || '',
          accountNumber: foundAccount.accountNumber || '',
          ifsc: foundAccount.ifsc || '',
        };
      }
    }

    // If vendorcodesheet me na mile, empty fields show (extras ignore for details)
    setBankCodeView(details || { vendorCode: vendorCode || '', bankCode: code || '' });
    setBankCodeViewOpen(true);
  };

  const sortedRows = useMemo(() => {
    const stabilized = groupedRows.map((r, idx) => [r, idx]);
    const cmp = (a, b) => {
      const ra = a[0];
      const rb = b[0];
      const dir = order === 'asc' ? 1 : -1;
      const key =
        orderBy === 'firmVendorBankCode' ? 'firmVendorBankCodes' : orderBy;
      const va = String(ra[key] || '').toLowerCase();
      const vb = String(rb[key] || '').toLowerCase();
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return a[1] - b[1];
    };
    stabilized.sort(cmp);
    return stabilized.map((x) => x[0]);
  }, [groupedRows, order, orderBy]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedRows]);

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setOrderBy(col);
    setOrder('asc');
  };

  const vendorCodeChipSx = () => ({
    bgcolor: 'rgba(15,118,110,0.08)',
    color: '#0f766e',
    fontWeight: 800,
  });

  const vendorInputSx = {
    '& .MuiInputLabel-root': {
      color: '#047857',
      fontWeight: 500,
      letterSpacing: 0.1,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#16a34a',
    },
    '& .MuiOutlinedInput-root': {
      position: 'relative',
      borderRadius: 1.5,
      backgroundColor: '#ffffff',
      boxShadow: 'none',
      transition: 'background-color 120ms ease-out',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderWidth: 0,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiOutlinedInput-root::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 2,
      background: '#e0e0e0',
      borderRadius: 0,
    },
    '& .MuiOutlinedInput-root:hover': {
      backgroundColor: '#ffffff',
    },
    '& .MuiOutlinedInput-root.Mui-focused::before': {
      background: '#e0e0e0',
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      backgroundColor: '#f3f4f6',
    },
    '& .MuiOutlinedInput-root.Mui-disabled::before': {
      background: '#e0e0e0',
    },
    '& .MuiOutlinedInput-root.Mui-disabled .MuiInputBase-input': {
      color: '#9ca3af',
    },
    '& .MuiInputLabel-root.Mui-disabled': {
      color: '#9ca3af',
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#6b7280',
      opacity: 1,
    },
    '& .MuiInputBase-input': {
      paddingTop: 1.5,
      paddingBottom: 1.25,
    },
  };

  const makeAutoCode = (kind, name, bankName, accountNumber) => {
    const typeLetter = kind === 'firm' ? 'F' : 'V';
    const firstAlpha = (s) => {
      const str = String(s || '')
        .trim()
        .toUpperCase();
      const m = str.match(/[A-Z]/);
      return m ? m[0] : 'X';
    };
    const digits = String(accountNumber || '').replace(/\D/g, '');
    const lastTwo = digits.slice(-2) || '00';
    return `${typeLetter}${firstAlpha(name)}${firstAlpha(bankName)}${lastTwo}`;
  };

  useEffect(() => {
    if (!autoCodeEnabled) return;
    if (editingRowId) return;
    const name = formFirmVendor;
    const bankName = formBankName;
    const accountNumber = formAccountNumber;
    if (!name && !bankName && !accountNumber) return;
    const nextCode = makeAutoCode(formKind, name, bankName, accountNumber);
    setFormFirmVendorCode(nextCode);
  }, [formKind, formFirmVendor, formBankName, formAccountNumber, autoCodeEnabled, editingRowId]);

  const openAddDialog = () => {
    setEditingRowId(null);
    setFormKind('vendor');
    setFormFirmVendorCode('');
    setFormFirmVendorBankCode('');
    setFormFirmVendor('');
    setFormBankName('');
    setFormAccountNumber('');
    setFormIfsc('');
    setFormContactDetails('');
    setFormVendorContactPerson('');
    setFormVendorContactPhone('');
    setFormApplicableCommissionPct('');
    setFormGstNumber('');
    setFormErrors({});
    setAutoCodeEnabled(true);
    setAddPageOpen(true);
  };

  const openEditDialog = (row) => {
    setAddPageOpen(false);
    setEditingRowId(row.id);
    const typeRaw = String(row.firmVendor || '').trim().toLowerCase();
    if (typeRaw === 'firm' || typeRaw === 'vendor') {
      setFormKind(typeRaw);
      setFormFirmVendor(row.firmVendorName || '');
    } else {
      setFormKind('vendor');
      setFormFirmVendor(row.firmVendorName || row.firmVendor || '');
    }
    setFormFirmVendorCode(row.firmVendorCode || '');
    setFormFirmVendorBankCode(row.firmVendorBankCode || '');
    setFormBankName(row.bankName || '');
    setFormAccountNumber(row.accountNumber || '');
    setFormIfsc(row.ifsc || '');
    setFormContactDetails(row.contactDetails || '');
    setFormVendorContactPerson(row.vendorContactPerson || '');
    setFormVendorContactPhone(row.vendorContactPersonPhone || '');
    setFormApplicableCommissionPct(
      typeof row.applicableCommissionPct === 'number'
        ? String(row.applicableCommissionPct)
        : row.applicableCommissionPct || ''
    );
    setFormGstNumber(row.gstNumber || '');
    setFormErrors({});
    setAutoCodeEnabled(false);
    setEditDialogOpen(true);
  };

  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveDialog();
    }
  };

  const validateForm = () => {
    const next = {};
    const code = formFirmVendorCode.trim();
    const bankCode = formFirmVendorBankCode.trim();
    const name = formFirmVendor.trim();
    const contact = formVendorContactPerson.trim();

    if (!code) next.firmVendorCode = 'Vendor code is required';
    if (!bankCode) next.firmVendorBankCode = 'Bank code is required';
    if (!name) next.firmVendor = 'Vendor name is required';
    if (!contact) next.vendorContactPerson = 'Contact person is required';

    const duplicateBankCode = rows.find(
      (r) =>
        r.firmVendorBankCode?.trim().toLowerCase() === bankCode.toLowerCase() && r.id !== editingRowId
    );
    if (duplicateBankCode) next.firmVendorBankCode = 'This bank code already exists';

    setFormErrors(next);
    return Object.keys(next).length === 0;
  };

  const saveDialog = () => {
    if (editingRowId && !canUpdate) return;
    if (!editingRowId && !canCreate) return;
    if (!validateForm()) return;

    const payload = {
      firmVendorCode: formFirmVendorCode.trim(),
      firmVendorBankCode: formFirmVendorBankCode.trim(),
      firmVendor: formKind === 'firm' ? 'Firm' : 'Vendor',
      firmVendorName: formFirmVendor.trim(),
      bankName: formBankName.trim() || undefined,
      accountNumber: formAccountNumber.trim() || undefined,
      vendorContactPerson: formVendorContactPerson.trim(),
      ifsc: formIfsc.trim() || undefined,
      contactDetails: formContactDetails.trim() || undefined,
      vendorContactPersonPhone: formVendorContactPhone.trim() || undefined,
      applicableCommissionPct: formApplicableCommissionPct.trim() || undefined,
      gstNumber: formGstNumber.trim() || undefined,
    };

    (async () => {
      setLoading(true);
      try {
        if (editingRowId) {
          const res = await fetch(`${API_BASE_URL}/api/vendors/${editingRowId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
            body: JSON.stringify(payload),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data?.error || 'Failed to update vendor');
          await loadVendors();
          setToast({ open: true, message: 'Vendor updated', severity: 'success' });
        } else {
          const res = await fetch(`${API_BASE_URL}/api/vendors`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
            body: JSON.stringify(payload),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data?.error || 'Failed to add vendor');
          await loadVendors();
          setToast({ open: true, message: 'Vendor added', severity: 'success' });
        }
        if (editingRowId) {
          setEditDialogOpen(false);
        } else {
          setAddPageOpen(false);
        }
      } catch (e) {
        setToast({ open: true, message: e?.message || 'Action failed', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  };

  const addBankCode = () => {
    const vc = (bankDialogVendor?.firmVendorCode || formFirmVendorCode).trim();
    const bc = newBankCode.trim();
    const bn = newBankName.trim();
    const an = newAccountNumber.trim();
    const ifsc = newIfsc.trim();
    if (!vc || !bc || !bn || !an || !ifsc) {
      setToast({ open: true, message: 'Fill bank code, name, account and IFSC', severity: 'error' });
      return;
    }
    const exists =
      Array.isArray(bankAccountsMap[vc]) &&
      bankAccountsMap[vc].some((acc) => String(acc.bankCode || '').toLowerCase() === bc.toLowerCase());
    if (exists) {
      setToast({ open: true, message: 'This bank code already exists', severity: 'error' });
      return;
    }
    const payload = {
      vendorCode: vc,
      bankCode: bc,
      bankName: bn,
      accountNumber: an,
      ifsc,
    };
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/vendor-bank-accounts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to add bank account');
        await loadBankAccounts();
        setNewBankCode('');
        setNewBankName('');
        setNewAccountNumber('');
        setNewIfsc('');
        setToast({ open: true, message: 'Bank account added', severity: 'success' });
      } catch (e) {
        setToast({ open: true, message: e?.message || 'Failed to add bank account', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  };

  const removeBankCode = (id) => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/vendor-bank-accounts/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to delete bank account');
        await loadBankAccounts();
        setToast({ open: true, message: 'Bank account removed', severity: 'info' });
      } catch (e) {
        setToast({ open: true, message: e?.message || 'Failed to delete bank account', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  };

  const confirmDelete = (rowId) => {
    if (!canDelete) return;
    setDeleteRowId(rowId);
    setDeleteDialogOpen(true);
  };

  const doDelete = () => {
    (async () => {
      const id = deleteRowId;
      if (!canDelete) return;
      if (!id) return;
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/vendors/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to delete vendor');
        setRows((prev) => prev.filter((r) => r.id !== id));
        setToast({ open: true, message: 'Vendor deleted', severity: 'info' });
      } catch (e) {
        setToast({ open: true, message: e?.message || 'Failed to delete', severity: 'error' });
      } finally {
        setLoading(false);
        setDeleteDialogOpen(false);
        setDeleteRowId(null);
      }
    })();
  };

  const exportCsv = () => {
    const header = [
      'Sr. No.',
      'Firm/Vendor Code',
      'Firm/Vendor Bank Codes',
      'Firm/Vendor',
      'Firm/Vendor Name',
      'Association',
      'Company Code',
      'Owner Percentage',
      'Bank Name',
      'Account Number',
      'IFSC',
      'Contact Details',
      'Vendor Contact Person',
      'Vendor Contact Person Phone',
      'Applicable Commision %',
      'GST Number',
    ];
    const sorted = [...rows].sort((a, b) =>
      String(a.firmVendorCode || '').localeCompare(String(b.firmVendorCode || ''))
    );
    const body = sorted
      .map((r, idx) => {
        const accounts = Array.isArray(bankAccountsMap[r.firmVendorCode]) ? bankAccountsMap[r.firmVendorCode] : [];
        const extraCodes = accounts.map((acc) => String(acc.bankCode || '')).filter(Boolean);
        const primaryCode = String(r.firmVendorBankCode || '').trim();
        const allCodes = Array.from(new Set([primaryCode, ...extraCodes].filter(Boolean))).join(',');
        const accountNumberRaw = String(r.accountNumber || '').replace(/,/g, '');
        const accountNumberClean = accountNumberRaw ? `="${accountNumberRaw}"` : '';
        return [
          idx + 1,
          r.firmVendorCode,
          allCodes,
          r.firmVendor,
          r.firmVendorName,
          r.association,
          r.companyCode,
          r.ownerPercentage,
          r.bankName,
          accountNumberClean,
          r.ifsc,
          r.contactDetails,
          r.vendorContactPerson,
          r.vendorContactPersonPhone,
          r.applicableCommissionPct,
          r.gstNumber,
        ]
          .map(toCsvValue)
          .join(',');
      })
      .join('\n');
    downloadTextFile({
      filename: 'vendors.csv',
      content: `${header.join(',')}\n${body}\n`,
      mime: 'text/csv;charset=utf-8',
    });
  };

  const downloadTemplate = () => {
    const header = [
      'Sr. No.',
      'Firm/Vendor Code',
      'Firm/Vendor Bank Code',
      'Firm/Vendor',
      'Firm/Vendor Name',
      'Association',
      'Company Code',
      'Owner Percentage',
      'Bank Name',
      'Account Number',
      'IFSC',
      'Contact Details',
      'Vendor Contact Person',
      'Vendor Contact Person Phone',
      'Applicable Commision %',
      'GST Number',
    ];
    const example = [
      '1',
      'VHT01',
      'VB001',
      'Himcon Travel',
      'Himcon Travel Pvt Ltd',
      'Association A',
      'C001',
      '50',
      'HDFC Bank',
      '1234567890',
      'HDFC0001234',
      'Some address/contact',
      'Sumit',
      '9876543210',
      '5',
      '07ABCDE1234F1Z5',
    ]
      .map(toCsvValue)
      .join(',');
    downloadTextFile({
      filename: 'vendors_template.csv',
      content: `${header.join(',')}\n${example}\n`,
      mime: 'text/csv;charset=utf-8',
    });
  };

  const triggerImport = () => {
    fileInputRef.current?.click?.();
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    try {
      const parsed = looksLikeExcelFile(file)
        ? await parseExcelFileToRows(file)
        : parseCsvText(await file.text());
      if (parsed.length === 0) throw new Error('Empty file');

      const header = parsed[0].map(normalizeHeaderCell);
      const body = parsed.slice(1);

      const idxSrNo = header.findIndex((h) => h === 'srno');
      const idxCode = header.findIndex((h) => h === 'firmvendorcode' || h === 'code');
      const idxBankCode = header.findIndex((h) => h === 'firmvendorbankcode' || h === 'bankcode');
      const idxKind = header.findIndex((h) => h === 'firmvendor' || h === 'type' || h === 'kind');
      const idxName = header.findIndex(
        (h) => h === 'firmvendorname' || h === 'vendorname' || h === 'name'
      );
      const idxContact = header.findIndex(
        (h) => h === 'vendorcontactperson' || h === 'contactperson' || h === 'contact'
      );
      const idxAssociation = header.findIndex((h) => h === 'association');
      const idxCompanyCode = header.findIndex((h) => h === 'companycode');
      const idxOwnerPercentage = header.findIndex((h) => h === 'ownerpercentage');
      const idxBankName = header.findIndex((h) => h === 'bankname');
      const idxAccountNumber = header.findIndex((h) => h === 'accountnumber');
      const idxIfsc = header.findIndex((h) => h === 'ifsc');
      const idxContactDetails = header.findIndex((h) => h === 'contactdetails');
      const idxVendorContactPhone = header.findIndex(
        (h) => h === 'vendorcontactpersonphone' || h === 'contactpersonphone' || h === 'contactphone'
      );
      const idxApplicableCommission = header.findIndex(
        (h) =>
          h === 'applicablecommissionpct' ||
          h === 'applicablecommission' ||
          h === 'applicablecommisionpct' ||
          h === 'applicablecommision'
      );
      const idxGstNumber = header.findIndex((h) => h === 'gstnumber' || h === 'gstno');

      const missingRequired =
        idxSrNo === -1 ||
        idxCode === -1 ||
        idxBankCode === -1 ||
        idxKind === -1 ||
        idxName === -1 ||
        idxAssociation === -1 ||
        idxCompanyCode === -1 ||
        idxOwnerPercentage === -1 ||
        idxBankName === -1 ||
        idxAccountNumber === -1 ||
        idxIfsc === -1 ||
        idxContactDetails === -1 ||
        idxContact === -1 ||
        idxVendorContactPhone === -1 ||
        idxApplicableCommission === -1 ||
        idxGstNumber === -1;

      if (missingRequired) {
        throw new Error(
          'CSV headers must match: Sr. No., Firm/Vendor Code, Firm/Vendor Bank Code, Firm/Vendor, Firm/Vendor Name, Association, Company Code, Owner Percentage, Bank Name, Account Number, IFSC, Contact Details, Vendor Contact Person, Vendor Contact Person Phone, Applicable Commision %, GST Number'
        );
      }

      const incoming = [];
      for (const row of body) {
        const kindCell = idxKind !== -1 ? String(row[idxKind] ?? '').trim() : '';
        const kindRaw = kindCell.toLowerCase();
        const kind = kindRaw === 'firm' ? 'firm' : 'vendor';

        let code = '';
        const bankCode = String(row[idxBankCode] ?? '').trim();
        const name = idxName !== -1 ? String(row[idxName] ?? '').trim() : '';
        const contact = String(row[idxContact] ?? '').trim();
        const association =
          idxAssociation !== -1 ? String(row[idxAssociation] ?? '').trim() : '';
        const companyCode =
          idxCompanyCode !== -1 ? String(row[idxCompanyCode] ?? '').trim() : '';
        const ownerPercentageRaw =
          idxOwnerPercentage !== -1 ? String(row[idxOwnerPercentage] ?? '').trim() : '';
        const bankName = idxBankName !== -1 ? String(row[idxBankName] ?? '').trim() : '';
        const accountNumber =
          idxAccountNumber !== -1 ? String(row[idxAccountNumber] ?? '').trim() : '';
        const ifsc = idxIfsc !== -1 ? String(row[idxIfsc] ?? '').trim() : '';
        const contactDetails =
          idxContactDetails !== -1 ? String(row[idxContactDetails] ?? '').trim() : '';
        const vendorContactPersonPhone =
          idxVendorContactPhone !== -1 ? String(row[idxVendorContactPhone] ?? '').trim() : '';
        const applicableCommissionRaw =
          idxApplicableCommission !== -1
            ? String(row[idxApplicableCommission] ?? '').trim()
            : '';
        const gstNumber =
          idxGstNumber !== -1 ? String(row[idxGstNumber] ?? '').trim() : '';

        const expectedCode = makeAutoCode(kind, name, bankName, accountNumber).toUpperCase();
        code = expectedCode;

        if (!code || !bankCode || !name || !contact) continue;

        incoming.push({
          firmVendorCode: code,
          firmVendorBankCode: bankCode,
          firmVendor: kindCell || (kind === 'firm' ? 'Firm' : 'Vendor'),
          firmVendorName: name,
          vendorContactPerson: contact,
          association: association || undefined,
          companyCode: companyCode || undefined,
          ownerPercentage: ownerPercentageRaw || undefined,
          bankName: bankName || undefined,
          accountNumber: accountNumber || undefined,
          ifsc: ifsc || undefined,
          contactDetails: contactDetails || undefined,
          vendorContactPersonPhone: vendorContactPersonPhone || undefined,
          applicableCommissionPct: applicableCommissionRaw || undefined,
          gstNumber: gstNumber || undefined,
        });
      }

      if (incoming.length === 0) throw new Error('No valid rows found in CSV');

      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/vendors/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify({ items: incoming }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Import failed');

      await loadVendors();
      setToast({
        open: true,
        message: `Import completed: ${data?.inserted ?? 0} added, ${data?.skipped ?? 0} skipped`,
        severity: 'success',
      });
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Import failed', severity: 'error' });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
      setLoading(false);
    }
  };

  return (
    <Layout title="Vendor Management">
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 650, letterSpacing: -0.3 }}>
              Vendor Management
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
              Manage firm vendor codes and contact persons. Import/export via CSV.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.25} justifyContent="flex-end" flexWrap="wrap">
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={openAddDialog}
              disabled={!canCreate}
            >
              Add Vendor
            </Button>
            <Button
              startIcon={<ExportIcon />}
              variant="contained"
              color="success"
              onClick={exportCsv}
              disabled={!canExport || loading}
            >
              Export
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              variant="contained"
              color="info"
              onClick={downloadTemplate}
              disabled={!canCreate || loading}
            >
              Template
            </Button>
            <Button
              startIcon={<ImportIcon />}
              variant="contained"
              color="inherit"
              onClick={triggerImport}
              sx={{ bgcolor: 'grey.700', color: 'common.white', '&:hover': { bgcolor: 'grey.800' } }}
              disabled={!canImport || loading}
            >
              Import
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: 'none' }}
              onChange={(e) => handleImportFile(e.target.files?.[0])}
            />
          </Stack>
        </Stack>

        {addPageOpen && !editingRowId && canCreate && (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 4,
              mb: 3,
              p: { xs: 2.5, md: 3 },
              borderColor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              sx={{ mb: 2.5 }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 750 }}>
                  Add Vendor
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Fill in the vendor details below and click Save.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={() => {
                    setAddPageOpen(false);
                  }}
                  color="inherit"
                >
                  Cancel
                </Button>
                <Button variant="contained" onClick={saveDialog} disabled={loading}>
                  Save
                </Button>
              </Stack>
            </Stack>

            <Stack spacing={2.5} onKeyDown={handleFormKeyDown}>
              <TextField
                select
                label="Type"
                value={formKind}
                onChange={(e) => setFormKind(e.target.value)}
                sx={vendorInputSx}
              >
                <MenuItem value="vendor">Vendor</MenuItem>
                <MenuItem value="firm">Firm</MenuItem>
              </TextField>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Firm Vendor Code"
                  value={formFirmVendorCode}
                  onChange={(e) => {
                    setFormFirmVendorCode(e.target.value);
                    setAutoCodeEnabled(false);
                  }}
                  error={Boolean(formErrors.firmVendorCode)}
                  helperText={formErrors.firmVendorCode || ' '}
                  autoFocus
                  fullWidth
                  sx={vendorInputSx}
                />
                <TextField
                  label="Firm Vendor Bank Code"
                  value={formFirmVendorBankCode}
                  onChange={(e) => setFormFirmVendorBankCode(e.target.value)}
                  error={Boolean(formErrors.firmVendorBankCode)}
                  helperText={formErrors.firmVendorBankCode || ' '}
                  fullWidth
                  sx={vendorInputSx}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Firm/Vendor Name"
                  value={formFirmVendor}
                  onChange={(e) => setFormFirmVendor(e.target.value)}
                  error={Boolean(formErrors.firmVendor)}
                  helperText={formErrors.firmVendor || ' '}
                  fullWidth
                  sx={vendorInputSx}
                />
                <TextField
                  label="Bank Name"
                  value={formBankName}
                  onChange={(e) => setFormBankName(e.target.value)}
                  fullWidth
                  sx={vendorInputSx}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Account Number"
                  value={formAccountNumber}
                  onChange={(e) => setFormAccountNumber(e.target.value)}
                  fullWidth
                  sx={vendorInputSx}
                />
                <TextField
                  label="IFSC"
                  value={formIfsc}
                  onChange={(e) => setFormIfsc(e.target.value)}
                  fullWidth
                  sx={vendorInputSx}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Vendor Contact Person"
                  value={formVendorContactPerson}
                  onChange={(e) => setFormVendorContactPerson(e.target.value)}
                  error={Boolean(formErrors.vendorContactPerson)}
                  helperText={formErrors.vendorContactPerson || ' '}
                  fullWidth
                  sx={vendorInputSx}
                />
                <TextField
                  label="Vendor Contact Person Phone"
                  value={formVendorContactPhone}
                  onChange={(e) => setFormVendorContactPhone(e.target.value)}
                  fullWidth
                  sx={vendorInputSx}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Applicable Commision %"
                  value={formApplicableCommissionPct}
                  onChange={(e) => setFormApplicableCommissionPct(e.target.value)}
                  fullWidth
                  sx={vendorInputSx}
                />
                <TextField
                  label="GST Number"
                  value={formGstNumber}
                  onChange={(e) => setFormGstNumber(e.target.value)}
                  fullWidth
                  sx={vendorInputSx}
                />
              </Stack>
              <TextField
                label="Contact Details"
                value={formContactDetails}
                onChange={(e) => setFormContactDetails(e.target.value)}
                multiline
                minRows={2}
                sx={vendorInputSx}
              />
            </Stack>
          </Paper>
        )}

        {!addPageOpen && (
          <>
          <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                select
                size="small"
                label="Type"
                value={filterKind}
                onChange={(e) => {
                  setFilterKind(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: { xs: '100%', md: 160 } }}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions.kinds.map((k) => (
                  <MenuItem key={k} value={k}>
                    {k.charAt(0).toUpperCase() + k.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Firm Vendor Code"
                value={filterVendorCode}
                onChange={(e) => {
                  setFilterVendorCode(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: { xs: '100%', md: 180 } }}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions.vendorCodes.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Firm Vendor Bank Code"
                value={filterBankCode}
                onChange={(e) => {
                  setFilterBankCode(e.target.value);
                  setPage(0);
                }}
                sx={{
                  minWidth: { xs: '100%', md: 200 },
                  '& .MuiInputLabel-root': {
                    maxWidth: 'calc(100% - 44px)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions.bankCodes.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                placeholder="Search code / bank code / vendor / contact…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: { xs: '100%', md: 320 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="text"
                color="inherit"
                onClick={() => {
                  setQuery('');
                  setFilterKind('');
                  setFilterVendorCode('');
                  setFilterBankCode('');
                  setPage(0);
                }}
                disabled={!query.trim() && !filterKind && !filterVendorCode && !filterBankCode}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Clear Filters
              </Button>
            </Stack>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              borderColor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: (t) =>
                  t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#eef2ff',
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                spacing={1.25}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontWeight: 750 }}>Vendors</Typography>
                  <Chip
                    size="small"
                    label={
                      rows.length === sortedRows.length
                        ? `${rows.length} total`
                        : `Showing ${sortedRows.length} of ${rows.length}`
                    }
                    sx={{
                      bgcolor: 'rgba(14,165,233,0.12)',
                      color: 'secondary.main',
                      fontWeight: 800,
                    }}
                  />
                </Stack>

 
              </Stack>
            </Box>
            <Divider />

            <TableContainer>
              <Table stickyHeader sx={{ minWidth: 780 }}>
                <TableHead>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: (t) =>
                          t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
                        color: (t) => (t.palette.mode === 'dark' ? 'common.white' : '#0f172a'),
                        fontWeight: 800,
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell sx={{ width: 180 }}>
                      <TableSortLabel
                        active={orderBy === 'firmVendorCode'}
                        direction={orderBy === 'firmVendorCode' ? order : 'asc'}
                        onClick={() => handleSort('firmVendorCode')}
                      >
                        Firm Vendor Code
                      </TableSortLabel>
                    </TableCell>
                    {/* Removed Firm Vendor Bank Code column */}
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'firmVendorName'}
                        direction={orderBy === 'firmVendorName' ? order : 'asc'}
                        onClick={() => handleSort('firmVendorName')}
                      >
                        Firm/Vendor Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'vendorContactPerson'}
                        direction={orderBy === 'vendorContactPerson' ? order : 'asc'}
                        onClick={() => handleSort('vendorContactPerson')}
                      >
                        Vendor Contact Person
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 200, bgcolor: '#0b60ff' }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {pagedRows.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      onClick={() => {
                        setViewRow(r);
                        setViewDialogOpen(true);
                      }}
                      sx={{
                        cursor: 'pointer',
                        '& td': { py: 2.25, fontSize: 15 },
                        '&:hover td': {
                          bgcolor: (t) =>
                            t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                        },
                      }}
                    >
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.firmVendorCode}
                          sx={{
                            ...vendorCodeChipSx(),
                            borderRadius: 2,
                          }}
                        />
                      </TableCell>
                      {/* Removed bank codes cell from list view */}
                      <TableCell>{r.firmVendorName || r.firmVendor}</TableCell>
                      <TableCell>{r.vendorContactPerson}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.75} justifyContent="center">
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditDialog(r.firstRow);
                              }}
                              disabled={!canUpdate || loading}
                              sx={{
                                borderRadius: 2,
                                color: '#0b60ff',
                                bgcolor: 'rgba(11,96,255,0.08)',
                                '&:hover': { bgcolor: 'rgba(11,96,255,0.14)' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Bank Accounts" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setBankDialogVendor(r.firstRow);
                                setNewBankCode('');
                                setNewBankName('');
                                setNewAccountNumber('');
                                setNewIfsc('');
                                setBankDialogOpen(true);
                              }}
                              disabled={loading}
                              sx={{
                                borderRadius: 2,
                                color: '#0f766e',
                                bgcolor: 'rgba(15,118,110,0.10)',
                                '&:hover': { bgcolor: 'rgba(15,118,110,0.16)' },
                              }}
                            >
                              <BankIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmDelete(r.firstRow.id);
                              }}
                              disabled={!canDelete || loading}
                              sx={{
                                borderRadius: 2,
                                color: 'error.main',
                                bgcolor: 'rgba(239,68,68,0.10)',
                                '&:hover': { bgcolor: 'rgba(239,68,68,0.16)' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}

                  {pagedRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 6 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 750 }}>
                            {query.trim() ? 'No matching vendors' : 'No vendors yet'}
                          </Typography>
                          <Typography sx={{ color: 'text.secondary', mt: 0.75 }}>
                            {query.trim()
                              ? 'Try a different search or clear the filter.'
                              : 'Add your first vendor or import from CSV.'}
                          </Typography>
                          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                            {query.trim() ? (
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  setQuery('');
                                  setPage(0);
                                }}
                              >
                                Clear search
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={openAddDialog}
                                disabled={!canCreate}
                              >
                                Add Vendor
                              </Button>
                            )}
                          </Stack>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />
            <TablePagination
              component="div"
              count={sortedRows.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Paper>
          </>
        )}
      </Box>

      <Dialog
        open={editDialogOpen && Boolean(editingRowId)}
        onClose={() => setEditDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Edit Vendor</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }} onKeyDown={handleFormKeyDown}>
            <TextField
              select
              label="Type"
              value={formKind}
              onChange={(e) => setFormKind(e.target.value)}
              sx={vendorInputSx}
            >
              <MenuItem value="vendor">Vendor</MenuItem>
              <MenuItem value="firm">Firm</MenuItem>
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Firm Vendor Code"
                value={formFirmVendorCode}
                onChange={(e) => {
                  setFormFirmVendorCode(e.target.value);
                  setAutoCodeEnabled(false);
                }}
                error={Boolean(formErrors.firmVendorCode)}
                helperText={formErrors.firmVendorCode || ' '}
                autoFocus
                fullWidth
                sx={vendorInputSx}
              />
              <TextField
                label="Firm Vendor Bank Code"
                value={formFirmVendorBankCode}
                onChange={(e) => setFormFirmVendorBankCode(e.target.value)}
                error={Boolean(formErrors.firmVendorBankCode)}
                helperText={formErrors.firmVendorBankCode || ' '}
                fullWidth
                sx={vendorInputSx}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Firm/Vendor Name"
                value={formFirmVendor}
                onChange={(e) => setFormFirmVendor(e.target.value)}
                error={Boolean(formErrors.firmVendor)}
                helperText={formErrors.firmVendor || ' '}
                fullWidth
                sx={vendorInputSx}
              />
              <TextField
                label="Bank Name"
                value={formBankName}
                onChange={(e) => setFormBankName(e.target.value)}
                fullWidth
                sx={vendorInputSx}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Account Number"
                value={formAccountNumber}
                onChange={(e) => setFormAccountNumber(e.target.value)}
                fullWidth
                sx={vendorInputSx}
              />
              <TextField
                label="IFSC"
                value={formIfsc}
                onChange={(e) => setFormIfsc(e.target.value)}
                fullWidth
                sx={vendorInputSx}
              />
            </Stack>
            
            <Stack direction="row" spacing={2}>
              <TextField
                label="Vendor Contact Person"
                value={formVendorContactPerson}
                onChange={(e) => setFormVendorContactPerson(e.target.value)}
                error={Boolean(formErrors.vendorContactPerson)}
                helperText={formErrors.vendorContactPerson || ' '}
                fullWidth
                sx={vendorInputSx}
              />
              <TextField
                label="Vendor Contact Person Phone"
                value={formVendorContactPhone}
                onChange={(e) => setFormVendorContactPhone(e.target.value)}
                fullWidth
                sx={vendorInputSx}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Applicable Commision %"
                value={formApplicableCommissionPct}
                onChange={(e) => setFormApplicableCommissionPct(e.target.value)}
                fullWidth
                sx={vendorInputSx}
              />
              <TextField
                label="GST Number"
                value={formGstNumber}
                onChange={(e) => setFormGstNumber(e.target.value)}
                fullWidth
                sx={vendorInputSx}
              />
            </Stack>
            <TextField
              label="Contact Details"
              value={formContactDetails}
              onChange={(e) => setFormContactDetails(e.target.value)}
              multiline
              minRows={2}
              sx={vendorInputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={saveDialog} variant="contained" disabled={!canUpdate || loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bankDialogOpen && Boolean(bankDialogVendor)}
        onClose={() => {
          setBankDialogOpen(false);
          setBankDialogVendor(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 900, fontSize: 30, pb: 1 }}>
          Bank Accounts
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {bankDialogVendor &&
            (() => {
              const vendorCode = String(bankDialogVendor.firmVendorCode || '').trim();
              const primaryBankCode = String(bankDialogVendor.firmVendorBankCode || '').trim();
              const extras = Array.isArray(bankAccountsMap[vendorCode]) ? bankAccountsMap[vendorCode] : [];
              const all = [];
              const seen = new Set();
              if (primaryBankCode) {
                const k = primaryBankCode.toLowerCase();
                if (!seen.has(k)) {
                  seen.add(k);
                  all.push({
                    id: `primary_${bankDialogVendor.id || vendorCode}_${primaryBankCode}`,
                    vendorCode,
                    bankCode: primaryBankCode,
                    bankName: bankDialogVendor.bankName || '',
                    accountNumber: bankDialogVendor.accountNumber || '',
                    ifsc: bankDialogVendor.ifsc || '',
                    isPrimary: true,
                  });
                }
              }
              extras.forEach((acc) => {
                const bc = String(acc.bankCode || '').trim();
                const k = bc.toLowerCase();
                if (!bc || seen.has(k)) return;
                seen.add(k);
                all.push({ ...acc, isPrimary: false });
              });

              const pillFieldSx = {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 999,
                  bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff'),
                },
              };

              const canAdd =
                !loading && newBankCode.trim() && newBankName.trim() && newAccountNumber.trim() && newIfsc.trim();

              const onEnterAdd = (e) => {
                if (e.key !== 'Enter') return;
                if (!canAdd) return;
                e.preventDefault();
                addBankCode();
              };

              return (
                <Stack spacing={2.25} sx={{ pt: 0.25 }}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f1f5f9'),
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.25}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Chip
                          size="small"
                          label={bankDialogVendor.firmVendorCode}
                          sx={{ fontWeight: 900, bgcolor: 'rgba(15,118,110,0.10)', color: '#0f766e' }}
                        />
                        <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                          {bankDialogVendor.firmVendorName || bankDialogVendor.firmVendor}
                        </Typography>
                      </Stack>
                      <Chip
                        size="small"
                        label={`Accounts: ${all.length}`}
                        sx={{ fontWeight: 900, bgcolor: 'rgba(99,102,241,0.12)', color: '#4338ca' }}
                      />
                    </Stack>
                  </Paper>

                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(148,163,184,0.10)' : '#ffffff'),
                      border: (t) => `1px solid ${t.palette.mode === 'dark' ? 'rgba(148,163,184,0.16)' : '#e2e8f0'}`,
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1.5}
                      alignItems={{ xs: 'stretch', md: 'center' }}
                    >
                      <TextField
                        label="Firm Bank Code"
                        value={newBankCode}
                        onChange={(e) => setNewBankCode(e.target.value)}
                        onKeyDown={onEnterAdd}
                        fullWidth
                        size="small"
                        sx={pillFieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <NumbersIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label="Bank Name"
                        value={newBankName}
                        onChange={(e) => setNewBankName(e.target.value)}
                        onKeyDown={onEnterAdd}
                        fullWidth
                        size="small"
                        sx={pillFieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BankIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label="Account Number"
                        value={newAccountNumber}
                        onChange={(e) => setNewAccountNumber(e.target.value)}
                        onKeyDown={onEnterAdd}
                        fullWidth
                        size="small"
                        sx={pillFieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccountIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label="IFSC"
                        value={newIfsc}
                        onChange={(e) => setNewIfsc(e.target.value)}
                        onKeyDown={onEnterAdd}
                        fullWidth
                        size="small"
                        sx={pillFieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <IfscIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', md: 'flex-start' } }}>
                        <Button
                          variant="contained"
                          onClick={addBankCode}
                          disabled={!canAdd}
                          sx={{
                            borderRadius: 999,
                            px: 2.75,
                            py: 1.1,
                            fontWeight: 900,
                            color: '#ffffff',
                            background: 'linear-gradient(90deg, #1e3a8a 0%, #7c3aed 100%)',
                            boxShadow: '0 8px 20px rgba(124,58,237,0.35)',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                              background: 'linear-gradient(90deg, #1d4ed8 0%, #8b5cf6 100%)',
                              boxShadow: '0 10px 24px rgba(124,58,237,0.45)',
                            },
                            '&.Mui-disabled': {
                              color: '#ffffff',
                              background: 'linear-gradient(90deg, #64748b 0%, #94a3b8 100%)',
                              boxShadow: 'none',
                              opacity: 0.85,
                            },
                          }}
                        >
                          Add Bank Account
                        </Button>
                      </Box>
                    </Stack>
                  </Paper>

                  <Paper sx={{ p: 0, borderRadius: 4, overflow: 'hidden' }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow
                            sx={{
                              bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(148,163,184,0.10)' : '#f8fafc'),
                            }}
                          >
                            <TableCell sx={{ fontWeight: 900 }}>Bank Code</TableCell>
                            <TableCell sx={{ fontWeight: 900 }}>Bank Name</TableCell>
                            <TableCell sx={{ fontWeight: 900 }}>Account Number</TableCell>
                            <TableCell sx={{ fontWeight: 900 }}>IFSC</TableCell>
                            <TableCell sx={{ fontWeight: 900 }} align="right">
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {all.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} sx={{ py: 4 }}>
                                <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                  No bank accounts added yet
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            all.map((acc) => {
                              const id = String(acc.id || '');
                              const canDeleteAcc = !acc.isPrimary && !id.startsWith('vc_');
                              return (
                                <TableRow key={id || `${vendorCode}_${acc.bankCode}`}>
                                  <TableCell>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Chip
                                        size="small"
                                        label={acc.bankCode}
                                        sx={{
                                          bgcolor: 'rgba(59,130,246,0.12)',
                                          color: '#1e40af',
                                          fontWeight: 800,
                                          borderRadius: 2,
                                        }}
                                      />
                                      {acc.isPrimary && (
                                        <Chip
                                          size="small"
                                          label="Primary"
                                          sx={{
                                            bgcolor: 'rgba(15,118,110,0.10)',
                                            color: '#0f766e',
                                            fontWeight: 800,
                                            borderRadius: 2,
                                          }}
                                        />
                                      )}
                                    </Stack>
                                  </TableCell>
                                  <TableCell>{acc.bankName || '-'}</TableCell>
                                  <TableCell>{acc.accountNumber || '-'}</TableCell>
                                  <TableCell>{acc.ifsc || '-'}</TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => removeBankCode(id)}
                                      disabled={!canDeleteAcc || loading}
                                      sx={{
                                        borderRadius: 2,
                                        bgcolor: 'rgba(239,68,68,0.10)',
                                        '&:hover': { bgcolor: 'rgba(239,68,68,0.16)' },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Stack>
              );
            })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBankDialogOpen(false); setBankDialogVendor(null); }} color="inherit">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewDialogOpen && Boolean(viewRow)}
        onClose={() => {
          setViewDialogOpen(false);
          setViewRow(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            pb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Vendor Details
          {viewRow && (
            <Chip
              size="small"
              label={viewRow.firmVendor || 'Vendor'}
              sx={{ fontWeight: 700, borderRadius: 999 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {viewRow && (
            <Stack spacing={2.5}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: (t) =>
                    t.palette.mode === 'dark' ? 'rgba(148,163,184,0.16)' : 'rgba(226,232,240,0.6)',
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Chip
                    size="small"
                    label={viewRow.firmVendorCode}
                    sx={{
                      ...vendorCodeChipSx(),
                      borderRadius: 2,
                      fontSize: 13,
                      px: 0.5,
                    }}
                  />
                </Stack>
              </Box>

              <Divider />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1.1fr' },
                  gap: 2,
                }}
              >
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Firm/Vendor Name
                    </Typography>
                    <Typography sx={{ fontWeight: 750, fontSize: 17 }}>
                      {viewRow.firmVendorName || viewRow.firmVendor}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Vendor Contact Person
                    </Typography>
                    <Typography>{viewRow.vendorContactPerson || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Contact Phone
                    </Typography>
                    <Typography>{viewRow.vendorContactPersonPhone || '-'}</Typography>
                  </Box>
                </Stack>

                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Bank Name
                    </Typography>
                    <Typography>{(viewRow.firstRow && viewRow.firstRow.bankName) || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Account Number
                    </Typography>
                    <Typography>{(viewRow.firstRow && viewRow.firstRow.accountNumber) || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      IFSC
                    </Typography>
                    <Typography>{(viewRow.firstRow && viewRow.firstRow.ifsc) || '-'}</Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 2,
                }}
              >
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Bank Codes
                  </Typography>
                  <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {getAllBankCodesForVendor(viewRow.firmVendorCode, viewRow.firstRow?.firmVendorBankCode).length === 0 ? (
                      <Typography sx={{ color: 'text.secondary' }}>No bank codes</Typography>
                    ) : (
                      getAllBankCodesForVendor(viewRow.firmVendorCode, viewRow.firstRow?.firmVendorBankCode).map((code) => (
                        <Chip
                          key={code}
                          label={code}
                          sx={{
                            bgcolor: 'rgba(59,130,246,0.14)',
                            color: '#1e40af',
                            fontWeight: 700,
                            borderRadius: 2,
                            cursor: 'pointer',
                          }}
                          onClick={() => openBankCodeDetails(viewRow.firmVendorCode, viewRow.firstRow, code)}
                        />
                      ))
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    GST Number
                  </Typography>
                  <Typography>{(viewRow.firstRow && viewRow.firstRow.gstNumber) || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Applicable Commission %
                  </Typography>
                  <Typography>
                    {viewRow.firstRow &&
                    viewRow.firstRow.applicableCommissionPct !== undefined &&
                    viewRow.firstRow.applicableCommissionPct !== null &&
                    viewRow.firstRow.applicableCommissionPct !== ''
                      ? viewRow.firstRow.applicableCommissionPct
                      : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Association
                  </Typography>
                  <Typography>{(viewRow.firstRow && viewRow.firstRow.association) || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Company Code
                  </Typography>
                  <Typography>{(viewRow.firstRow && viewRow.firstRow.companyCode) || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Owner Percentage
                  </Typography>
                  <Typography>
                    {viewRow.firstRow &&
                    viewRow.firstRow.ownerPercentage !== undefined &&
                    viewRow.firstRow.ownerPercentage !== null &&
                    viewRow.firstRow.ownerPercentage !== ''
                      ? viewRow.firstRow.ownerPercentage
                      : '-'}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Contact Details
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-line' }}>
                  {(viewRow.firstRow && viewRow.firstRow.contactDetails) || '-'}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => {
              setViewDialogOpen(false);
              setViewRow(null);
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bankCodeViewOpen && Boolean(bankCodeView)}
        onClose={() => {
          setBankCodeViewOpen(false);
          setBankCodeView(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            pb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Bank Code Details
          {bankCodeView && (
            <Chip
              size="small"
              label={bankCodeView.bankCode || ''}
              sx={{ fontWeight: 700, borderRadius: 999 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {bankCodeView && (
            <Stack spacing={2.5}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1.1fr' },
                  gap: 2,
                }}
              >
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Firm/Vendor Name
                    </Typography>
                    <Typography sx={{ fontWeight: 750, fontSize: 17 }}>
                      {bankCodeView.vendorName || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Bank Name
                    </Typography>
                    <Typography>{bankCodeView.bankName || '-'}</Typography>
                  </Box>
                </Stack>
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Account Number
                    </Typography>
                    <Typography>{bankCodeView.accountNumber || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      IFSC
                    </Typography>
                    <Typography>{bankCodeView.ifsc || '-'}</Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => {
              setBankCodeViewOpen(false);
              setBankCodeView(null);
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete Vendor?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary' }}>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={doDelete} variant="contained" color="error" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
